import { useHandleResponse } from '@/utils/useHandleResponse';
import { DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Col, DatePicker, Input, Row, Select, Spin, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../context/AuthProvider';
import {
  setSelectedRecords,
  updatePropsAll,
} from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setUsers } from '../../../../contextRedux/User/UserStore';
import { AnimateButton } from '../../../../customComponents/button/CustomButton';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import UserService from '../../../../services/user/UserService';
import { RootState } from '../../../../store';
import './MentenancePlanningSelectionTab.css';

const MentenancePlanningSelectionTab: React.FC = () => {
  const dispatch = useDispatch();
  const selectedRecords = useSelector((state: RootState) => state?.models?.mentenancePlanning?.selectedRecords);
  const users = useSelector((state: RootState) => state?.store?.user?.data);

  const [filteredRecords, setFilteredRecords] = useState<any[]>([]);
  const handleResponse = useHandleResponse();
  const { profile } = useAuth();

  const STORAGE_KEY = 'mentenance_planning_selections';
  const EXPIRATION_TIME = 3 * 60 * 1000; // 3 minutes in milliseconds

  // Fetch users (technicians) for the dropdown
  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician, senior_tehnician',
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    setFilteredRecords(selectedRecords);
  }, [selectedRecords]);

  // Update the useEffect for loading selections
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && savedData.length > 0 && savedData.length !== selectedRecords.length) {
      dispatch(setSelectedRecords(savedData));
    }
  }, [dispatch, selectedRecords.length]);

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost planificata cu succes`);
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  // Update handleChange
  const handleChange = (value: any, field: string, recordIndex: number) => {
    const updatedRecords = [...filteredRecords];
    const technician = field === 'assignedTechnician' ? users.find((u: any) => u.name === value) : null;

    updatedRecords[recordIndex] = {
      ...updatedRecords[recordIndex],
      [field]: value,
      ...(technician && {
        assignedTechnicianId: technician.id,
        assignedTechnician: technician.name,
      }),
    };

    setFilteredRecords(updatedRecords);
    dispatch(setSelectedRecords(updatedRecords));
    saveToStorage(updatedRecords);
  };

  // Update handleGlobalUpdate
  const handleGlobalUpdate = (field: string, value: any) => {
    const technician = field === 'assignedTechnician' ? users.find((u) => u.name === value) : null;

    const updatedRecords = selectedRecords.map((record: any) => ({
      ...record,
      [field]: value,
      ...(technician && {
        assignedTechnicianId: technician.id,
        assignedTechnician: technician.name,
      }),
    }));

    setFilteredRecords(updatedRecords);
    dispatch(setSelectedRecords(updatedRecords));
    saveToStorage(updatedRecords);
  };

  // Update handleRemoveRecord
  const handleRemoveRecord = (recordNumber: string) => {
    const updatedRecords = filteredRecords.filter((record) => record.number !== recordNumber);
    setFilteredRecords(updatedRecords);
    dispatch(setSelectedRecords(updatedRecords));
    saveToStorage(updatedRecords);
  };

  // Filter records based on selected values
  const handleFilter = (field: string, value: any) => {
    if (value === 'all') {
      setFilteredRecords(selectedRecords);
      return;
    }

    // Get all selected records from Redux
    const allSelectedRecords = selectedRecords;

    // Filter the records based on the selected value
    const filtered = allSelectedRecords.filter((record: any) =>
      field === 'mentenancePlanningSystemTypes'
        ? record?.mentenancePlanningSystemTypes?.some((type: any) => type?.systemType?.name === value)
        : record[field]?.name === value
    );

    // Update the filtered records while maintaining the full selection in Redux
    setFilteredRecords(filtered);
  };

  // Update handleSelectedRecords
  const handleSelectedRecords = async () => {
    const promises = selectedRecords.map(
      (record, index) =>
        new Promise((resolve) => {
          setTimeout(() => {
            saveMentenancePlanning({
              ...record,
              registeredByUser: profile,
              registeredByUserId: profile?.id,
              statusId: 2,
              history: [`[Inregistrata] - ${dayjs().format('DD-MM-YYYY HH:mm')}`],
            });
            resolve(true);
          }, index * 100); // stagger requests 100ms apart
        })
    );

    await Promise.allSettled(promises);

    dispatch(updatePropsAll({ field: 'refresh', value: true }));
    setFilteredRecords([]);
    dispatch(setSelectedRecords([]));
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const uniqueBeneficiars = useMemo(() => {
    const beneficiarNames = selectedRecords.map((record) => record?.company?.name);
    return Array.from(new Set(beneficiarNames)); // Convert Set to Array
  }, [selectedRecords]);

  const uniquePuncts = useMemo(() => {
    const punctNames = selectedRecords.map((record) => record?.workPlace?.name);
    return Array.from(new Set(punctNames)); // Convert Set to Array
  }, [selectedRecords]);

  const uniqueSistems = useMemo(() => {
    const sistemNames = selectedRecords.flatMap(
      (record) => record?.mentenancePlanningSystemTypes?.map((type: any) => type?.systemType?.name) || []
    );
    return Array.from(new Set(sistemNames)); // Remove duplicates
  }, [selectedRecords]);

  useEffect(() => {
    const formattedRecords = selectedRecords.map((record) => ({
      ...record,
      appointment: record.appointment ? dayjs(record.appointment) : null,
    }));
    setFilteredRecords(formattedRecords);
  }, [selectedRecords]);

  // ... existing code ...

  // Update the useEffect for loading selections
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && savedData.length > 0 && savedData.length !== selectedRecords.length) {
      dispatch(setSelectedRecords(savedData));
    }
  }, [dispatch, selectedRecords.length]);

  // Add these utility functions at the top of the component
  const sanitizeData = (data: any[]) => {
    return data.map((item) => ({
      number: item.number,
      company: { name: item.company?.name },
      workPlace: { name: item.workPlace?.name },
      mentenancePlanningSystemTypes: item.mentenancePlanningSystemTypes?.map((type: any) => ({
        systemType: { name: type.systemType?.name },
      })),
      appointment: item.appointment,
      intervalTimetable: item.intervalTimetable,
      assignedTechnician: item.assignedTechnician,
      periodicity: item.periodicity,
    }));
  };

  const saveToStorage = (data: any[]) => {
    const storageData = {
      data: sanitizeData(data),
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  };

  const loadFromStorage = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const { data, timestamp } = JSON.parse(stored);

      // Check if data is expired
      if (Date.now() - timestamp > EXPIRATION_TIME) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error loading from storage:', error);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  // Define columns with editable fields
  const columns = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag>, width: 280 },

    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
      width: 80,
    },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
      width: 280,
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
      width: 280,
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Tehnician',
      dataIndex: 'assignedTechnician',
      editable: true,
      render: (_value: any, record: any, index: any) => {
        return (
          <Select
            value={record?.assignedTechnician}
            style={{ width: '250px' }}
            placeholder='Select Tehnician'
            showSearch
            onChange={(value) => {
              handleChange(value, 'assignedTechnician', index);
            }}
            filterOption={(input: string, option: any) =>
              option?.children?.toLowerCase()?.includes(input.toLowerCase())
            }
          >
            {users?.map((user: any) => (
              <Select.Option key={user?.id} value={user?.name}>
                {user?.name}
              </Select.Option>
            ))}
          </Select>
        );
      },
      width: 280,
    },
    {
      title: 'Ziua Programarii',
      dataIndex: '',
      editable: true,
      render: (_value: any, record: any, index: any) => {
        return (
          <>
            <Row style={{ display: 'grid', rowGap: '10px' }}>
              <Col>
                <DatePicker
                  format='DD-MM-YYYY'
                  value={
                    selectedRecords && selectedRecords[index] && selectedRecords[index].appointment
                      ? dayjs(selectedRecords[index].appointment)
                      : dayjs()
                  }
                  style={{ width: '250px' }}
                  placeholder='Select Ziua Programarii'
                  onChange={(date) => {
                    handleChange(date ? date : dayjs(), 'appointment', index);
                  }}
                />
              </Col>
              <Col>
                <Input
                  style={{ width: '250px' }}
                  placeholder='Ex. Interval orar 9 - 12:30'
                  value={record.intervalTimetable}
                  onChange={(e: any) => handleChange(e.target.value, 'intervalTimetable', index)}
                />
              </Col>
            </Row>
          </>
        );
      },
      width: 280,
    },
    {
      title: 'Actiuni',
      dataIndex: 'actions',
      render: (_value: any, record: any) => (
        <Button type='link' icon={<DeleteOutlined />} onClick={() => handleRemoveRecord(record.number)} danger></Button>
      ),
      width: 100,
    },
  ];

  if (isSavingMentenancePlanning || isUsersLoading) {
    return <Spin className='spinnner' size='large' />;
  }

  return (
    <div className='table-page-no-h-scroll'>
      {selectedRecords.length > 0 &&
        !selectedRecords.some((sr: any) => sr.assignedTechnician == null || sr.appointment == null) && (
          <AnimateButton
            onClick={handleSelectedRecords}
            style={{ float: 'right', marginBottom: '0.5%', marginRight: '1%' }}
          >
            Programeaza Mentenante
          </AnimateButton>
        )}
      <div className='table-scroll-wrapper'>
        <Table
          bordered
          dataSource={filteredRecords}
          columns={columns.map((col: any) => ({
            ...col,
            onCell: (record: any) => ({
              record,
              editable: col.editable ?? false,
              title: col.title,
              children: record[col.dataIndex],
              column: col,
            }),
          }))}
          rowKey='number'
          pagination={false}
          rowClassName='editable-row'
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* Global Update Section */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 25, rowGap: '10px' }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            style={{ width: '100%' }}
            placeholder='Select Beneficiar'
            onChange={(value) => handleFilter('company', value)} // Filter records by Beneficiar
          >
            <Select.Option value='all'>All</Select.Option>
            {uniqueBeneficiars.map((beneficiar: any) => (
              <Select.Option key={beneficiar} value={beneficiar}>
                {beneficiar}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            style={{ width: '100%' }}
            placeholder='Select Punct'
            onChange={(value) => handleFilter('workPlace', value)} // Filter records by Punct
          >
            <Select.Option value='all'>All</Select.Option>
            {uniquePuncts.map((punct: any) => (
              <Select.Option key={punct} value={punct}>
                {punct}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            style={{ width: '100%' }}
            placeholder='Select Sistem'
            onChange={(value) => handleFilter('mentenancePlanningSystemTypes', value)} // Filter records by Sistem
          >
            <Select.Option value='all'>All</Select.Option>
            {uniqueSistems.map((sistem: any) => (
              <Select.Option key={sistem} value={sistem}>
                {sistem}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            style={{ width: '100%' }}
            placeholder='Select Tehnician Bulk'
            showSearch
            filterOption={(input: string, option: any) =>
              option?.children?.toLowerCase()?.includes(input.toLowerCase())
            }
            onChange={(value) => {
              handleGlobalUpdate('assignedTechnician', value);
            }}
          >
            {users?.map((user: any) => (
              <Select.Option key={user?.id} value={user?.name}>
                {user?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <DatePicker
            style={{ width: '100%' }}
            placeholder='Select Ziua Programarii Bulk'
            format='DD-MM-YYYY'
            onChange={(date, _dateString) => {
              if (date) {
                handleGlobalUpdate('appointment', date);
              } else {
                handleGlobalUpdate('appointment', null);
              }
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Input
            style={{ width: '100%' }}
            placeholder='Ex. Interval orar 9 - 12:30 Bulk'
            onChange={(e) => handleGlobalUpdate('intervalTimetable', e.target.value)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default MentenancePlanningSelectionTab;
